import React, { Fragment, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form/dist/react-hook-form.ie11";

import { calendarActions, selectSelectedEventId } from "@hubcapp/state";
import { useTealium } from "@hubcapp/context";

import { EditTimeOffFormFields } from "./types";
import { fireOnce } from "@ds-developer/frontend";
import { isEventAllDay } from "./helpers";
import { useSnackbar } from "notistack";
import DeleteTimeOffConfirmationModal from "../DeleteTimeOffConfirmationModal";
import EditTimeOffModal from "./EditTimeOffModal";
import useMutationUpdateTimeOff from "@hubcapp/components/Calendar/components/Modals/EditTimeOffModal/hooks/useMutationUpdateTimeOff";
import useQueryUnavailableEvent from "./hooks/useQueryUnavailableEvent";
import validationSchema from "./validationSchema";

const StateProvider = () => {
  const dispatch = useDispatch();
  const selectedEventId = useSelector(selectSelectedEventId);
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading } = useQueryUnavailableEvent(selectedEventId);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [
    updateMutation,
    { loading: mutationLoading, error: mutationError },
  ] = useMutationUpdateTimeOff();
  const event = data?.timeOff;
  const allDay = event && isEventAllDay(event);
  const { trackEvent } = useTealium();

  if (mutationError) {
    enqueueSnackbar("Unable to update time-off", {
      variant: "error",
    });
  }

  const {
    errors,
    formState: { isValid },
    handleSubmit,
    register,
    setValue,
  } = useForm({
    mode: "onChange",
    validationSchema,
  });

  useEffect(() => {
    if (event) {
      setValue("notes", event.notes);
    }
  }, [event]);

  const submit = (values: EditTimeOffFormFields) => {
    trackEvent({
      eventCategory: "Calendar - Time Off",
      eventAction: "Button Click",
      eventLabel: "Save",
    });
    setSubmitting(true);
    updateMutation({
      variables: { id: event.id, ...values },
    }).then(() => {
      setSubmitting(false);
      dispatch(calendarActions.calendarEventClosed());
    });
  };

  const handleDeleteEventClicked = () => {
    trackEvent({
      eventCategory: "Calendar - Time Off",
      eventAction: "Button Click",
      eventLabel: "Delete Time Off",
    });
    setShowConfirmationModal(true);
  };

  const handleDeleteSubmit = () => {
    dispatch(calendarActions.calendarEventClosed());
  };

  const handleDeleteCancel = () => {
    setShowConfirmationModal(false);
  };

  const onHandleNotesEventClick = () => {
    fireOnce("Calendar - Time Off|Interaction|Notes", () => {
      trackEvent({
        eventCategory: "Calendar - Time Off",
        eventAction: "Interaction",
        eventLabel: "Notes",
      });
    });
  };

  return (
    <Fragment>
      {showConfirmationModal ? (
        <DeleteTimeOffConfirmationModal
          eventId={event.id}
          onSubmit={handleDeleteSubmit}
          onCancel={handleDeleteCancel}
          repeat={event?.repeatFrequency}
        />
      ) : (
        <EditTimeOffModal
          loading={loading}
          updating={mutationLoading}
          allDay={allDay}
          startTime={event?.startDateTime && new Date(event.startDateTime)}
          endTime={event?.endDateTime && new Date(event.endDateTime)}
          repeat={event?.repeatFrequency || ""}
          onDeleteEventClicked={handleDeleteEventClicked}
          onSaveClicked={handleSubmit(submit)}
          submitting={submitting}
          register={register}
          errors={errors}
          isValid={isValid}
          onHandleNotesEventClick={onHandleNotesEventClick}
        />
      )}
    </Fragment>
  );
};

export default StateProvider;
